/**
 * IndianFamilyTreeStory — the "why we exist" article page for
 * IndianFamilyTree.com. Reachable at /indian-family-tree/story (canonical)
 * or via the saffron tagline strip on the IFT home (each of the four
 * captions deep-links to its matching section anchor):
 *
 *   /indian-family-tree/story#discover-your-roots
 *   /indian-family-tree/story#celebrate-your-heritage
 *   /indian-family-tree/story#preserve-your-legacy
 *   /indian-family-tree/story#celebrate-every-indian-life
 *
 * Verbatim copy supplied by the founder — do not rewrite without asking.
 * Design mirrors the IFT saffron/parchment palette from
 * IndianFamilyTreeHome so the article feels like part of the same
 * editorial experience.
 */
import { useEffect, useRef } from "react";
import { Helmet } from "react-helmet-async";
import { Link, useLocation } from "react-router-dom";
import { ArrowRight, TreePine, Flower2 } from "lucide-react";
import ArticleReaderToolbar from "@/components/reader/ArticleReaderToolbar";
import SplinkCard from "@/components/SplinkCard";

// WhatsApp brand green — used only where a genuine WhatsApp CTA earns
// the recognition (share-to-family flows). Do not repurpose for
// unrelated buttons; brand consistency matters.
const WA_GREEN = "#25D366";
const WA_GREEN_DEEP = "#128C7E";

// Inline SVG so we don't ship a raster logo and can tint via currentColor.
function WhatsAppIcon({ className = "w-4 h-4" }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden>
      <path d="M17.5 14.4c-.3-.15-1.7-.85-2-.94-.27-.1-.47-.15-.66.15s-.75.94-.92 1.13c-.17.2-.34.22-.63.07-.3-.15-1.24-.46-2.36-1.46-.87-.78-1.46-1.74-1.63-2.03-.17-.3-.02-.46.13-.6.13-.13.3-.34.44-.51.15-.17.2-.29.3-.49.1-.2.05-.37-.02-.52-.07-.15-.66-1.6-.9-2.19-.24-.57-.48-.5-.66-.5h-.56c-.2 0-.51.07-.78.37s-1.03 1-1.03 2.44 1.05 2.83 1.2 3.03c.15.2 2.08 3.18 5.05 4.46.7.3 1.26.48 1.69.62.71.23 1.35.2 1.86.12.57-.08 1.7-.7 1.95-1.36.24-.66.24-1.23.17-1.35-.07-.12-.27-.2-.56-.34zM12 2.5A9.5 9.5 0 0 0 3.7 16.9L2.5 21.5l4.72-1.24A9.5 9.5 0 1 0 12 2.5zm0 17.3a7.79 7.79 0 0 1-3.97-1.09l-.28-.17-2.8.74.75-2.73-.19-.29a7.8 7.8 0 1 1 6.49 3.54z" />
    </svg>
  );
}

// Build a WhatsApp share URL. Uses `https://wa.me/?text=...` which the
// mobile app / web WhatsApp both understand — no phone number attached
// so the user picks whom to send to. Origin is read at click-time (not
// build-time) so the same code works across all sister-app hostnames.
function buildWhatsAppShareUrl(text) {
  return `https://wa.me/?text=${encodeURIComponent(text)}`;
}

const P = {
  bg:         "#FAF3E6",     // parchment
  bgDeep:     "#F1E6CE",     // slightly warmer parchment
  ink:        "#2E1A05",     // deep bark ink
  saffron:    "#FF9933",     // Indian flag saffron
  saffronDeep:"#C25A1A",     // deep saffron / earth
  divider:    "#8A3F22",
  card:       "#FFFDF8",
};

const SECTIONS = [
  {
    id:      "discover-your-roots",
    kicker:  "01",
    title:   "Discover Your Roots",
    lede:    "Every family has a story—and every story begins somewhere.",
    body: [
      "IndianFamilyTree.com helps you explore the people, places, relationships, and memories that shaped your family. Start with the people you know—parents, grandparents, siblings, aunts, uncles, cousins—and gradually reach backward through generations.",
      "Record names, maiden names, nicknames, birthplaces, ancestral villages, family homes, occupations, photographs, documents, and stories. Connect relatives across India and around the world and work together to fill the missing branches of your family tree.",
      "Your roots are more than names and dates. They are the journeys, sacrifices, traditions, relationships, and experiences that brought your family to where it is today.",
    ],
    close:   "Discover where you came from. Connect the generations. Find your family story.",
    waTeaser: "🪷 Every family has a story. Let's build ours together on IndianFamilyTree.com — starting with the people we already know.",
  },
  {
    id:      "celebrate-your-heritage",
    kicker:  "02",
    title:   "Celebrate Your Heritage",
    lede:    "Heritage is what connects one generation to the next.",
    body: [
      "India's extraordinary diversity is reflected in its families—the languages we speak, foods we prepare, festivals we celebrate, places we call home, family customs we follow, and stories handed down through generations.",
      "IndianFamilyTree.com provides a place to document and share these traditions before they disappear. Preserve family recipes, photographs, migration stories, ancestral homes, celebrations, wedding traditions, occupations, community histories, and treasured memories.",
      "Families that have moved to other parts of India or across the world can give younger generations a meaningful connection to the places and people from which their family came.",
    ],
    close:   "Remember your traditions. Share your stories. Celebrate the heritage that makes your family unique.",
    waTeaser: "🪷 The recipes, festivals, and stories that make our family who we are — let's preserve them together on IndianFamilyTree.com.",
  },
  {
    id:      "preserve-your-legacy",
    kicker:  "03",
    title:   "Preserve Your Legacy",
    lede:    "Memories can fade. Photographs can be lost. Stories disappear when the people who remember them are no longer here.",
    body: [
      "IndianFamilyTree.com is designed to help families preserve their history for generations to come.",
      "Build family profiles, organize photographs, record important dates and relationships, document achievements, preserve family stories, and create lasting memorials for loved ones who have passed away.",
      "Encourage parents and grandparents to tell their stories in their own words. Record where they grew up, what life was like, the challenges they faced, the work they did, the people they loved, and the lessons they want future generations to remember.",
      "What may seem ordinary today could become priceless family history fifty or a hundred years from now.",
    ],
    close:   "Preserve today what tomorrow's generations will treasure.",
    waTeaser: "🪷 Photographs fade. Stories disappear. Let's record our family history on IndianFamilyTree.com — before the memories are gone.",
  },
  {
    id:      "celebrate-every-indian-life",
    kicker:  "04",
    title:   "Celebrate Every Indian Life",
    lede:    "Every life is part of India's story.",
    body: [
      "IndianFamilyTree.com believes that family history should not be limited to famous people, wealthy families, or historical personalities. Every parent, grandparent, farmer, teacher, homemaker, worker, entrepreneur, artist, soldier, caregiver, and ordinary citizen has a story worth preserving.",
      "Create profiles for living family members and lasting memorials for those who have passed away. Record their photographs, relationships, achievements, struggles, humorous moments, favorite memories, and the difference they made in the lives of others.",
      "A person's importance cannot always be measured by public recognition. Sometimes an individual's greatest legacy is simply the family they raised, the people they helped, the values they passed on, and the memories they left behind.",
      "IndianFamilyTree.com is envisioning to become a living archive of generations of Indian families—in India and throughout the global Indian diaspora.",
    ],
    close:   "Every person matters. Every family has a story. Every life deserves to be remembered.",
    waTeaser: "🪷 Every person matters. Every family has a story. Help me build our family's chapter on IndianFamilyTree.com.",
  },
];

export default function IndianFamilyTreeStory() {
  const { hash } = useLocation();
  const storyBodyRef = useRef(null);

  // Smooth-scroll to the matching anchor when the page loads with a
  // hash from the tagline strip. Standard browser anchor jump would
  // land the target flush with the viewport top and hide it behind
  // the sticky header; a manual scrollIntoView with a small offset
  // keeps the section title comfortably visible.
  useEffect(() => {
    if (!hash) { window.scrollTo({ top: 0, behavior: "auto" }); return; }
    const id = hash.replace(/^#/, "");
    const el = document.getElementById(id);
    if (!el) return;
    const y = el.getBoundingClientRect().top + window.scrollY - 96;
    window.scrollTo({ top: y, behavior: "smooth" });
  }, [hash]);

  return (
    <div style={{ background: P.bg }} data-testid="ift-story-page">
      <Helmet>
        <title>Our Story — Discover · Celebrate · Preserve — IndianFamilyTree.com</title>
        <meta
          name="description"
          content="Why IndianFamilyTree.com exists: helping every Indian family, in India and across the diaspora, discover their roots, celebrate their heritage, and preserve their legacy."
        />
      </Helmet>

      {/* ── Hero ─────────────────────────────────────────────────── */}
      <section
        className="relative overflow-hidden"
        style={{ background: `linear-gradient(180deg, ${P.bgDeep} 0%, ${P.bg} 100%)` }}
        data-testid="ift-story-hero"
      >
        <div className="max-w-4xl mx-auto px-4 lg:px-8 py-16 lg:py-24 text-center">
          <div
            className="text-[11px] font-semibold uppercase tracking-[0.28em]"
            style={{ color: P.saffronDeep }}
            data-testid="ift-story-eyebrow"
          >
            IndianFamilyTree.com<sup className="ml-0.5 text-[8px]">™</sup>
          </div>
          <h1
            className="mt-4 font-display leading-[1.05] tracking-tight text-4xl sm:text-5xl lg:text-6xl"
            style={{ color: P.ink, fontFamily: "'Playfair Display', Georgia, serif" }}
            data-testid="ift-story-title"
          >
            Discover · Celebrate · Preserve
          </h1>
          <p
            className="mt-6 max-w-2xl mx-auto text-lg leading-relaxed"
            style={{ color: "#4A2A0F" }}
          >
            A living archive for every Indian family and home — in India and
            across the global Indian diaspora.
          </p>

          {/* Quick jump nav — mirrors the saffron strip on the home page */}
          <nav
            className="mt-10 inline-flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-[11px] sm:text-xs font-semibold uppercase tracking-[0.18em]"
            style={{ fontFamily: "'Playfair Display', Georgia, serif", color: P.ink }}
            data-testid="ift-story-toc"
          >
            {SECTIONS.map((s, i) => (
              <span key={s.id} className="inline-flex items-center gap-4">
                <a
                  href={`#${s.id}`}
                  className="hover:underline"
                  data-testid={`ift-story-toc-${s.id}`}
                >
                  {s.title}
                </a>
                {i < SECTIONS.length - 1 && (
                  <span aria-hidden style={{ color: P.divider }}>◆</span>
                )}
              </span>
            ))}
          </nav>
        </div>
      </section>

      {/* ── Sections ─────────────────────────────────────────────── */}
      <div className="max-w-3xl mx-auto px-4 lg:px-6 py-12 lg:py-20 space-y-16 lg:space-y-24">
        <ArticleReaderToolbar
          bodyRef={storyBodyRef}
          title="Discover · Celebrate · Preserve — IndianFamilyTree.com"
          source_lang="en"
          storageKey="story:ift"
        />
        <div ref={storyBodyRef} className="space-y-16 lg:space-y-24">
        {SECTIONS.map((s) => (
          <article
            key={s.id}
            id={s.id}
            className="scroll-mt-24"
            data-testid={`ift-story-section-${s.id}`}
          >
            <div
              className="text-xs font-mono tracking-[0.28em]"
              style={{ color: P.saffronDeep }}
            >
              {s.kicker} — Chapter
            </div>
            <h2
              className="mt-2 font-display leading-[1.05] tracking-tight text-3xl sm:text-4xl lg:text-5xl"
              style={{ color: P.ink, fontFamily: "'Playfair Display', Georgia, serif" }}
            >
              {s.title}
            </h2>
            <p
              className="mt-4 text-xl italic leading-relaxed"
              style={{ color: "#4A2A0F", fontFamily: "'Playfair Display', Georgia, serif" }}
            >
              {s.lede}
            </p>
            <div className="mt-6 space-y-5 text-base sm:text-[17px] leading-[1.75]" style={{ color: "#3A200A" }}>
              {s.body.map((p, idx) => (
                <p key={idx}>{p}</p>
              ))}
            </div>
            <p
              className="mt-8 font-semibold text-lg"
              style={{ color: P.saffronDeep, fontFamily: "'Playfair Display', Georgia, serif" }}
            >
              {s.close}
            </p>

            {/* Per-chapter WhatsApp share — Indian diaspora families forward
                heritage content on WhatsApp far more than any other channel.
                One-tap "Send this chapter to family" is the acquisition
                loop: teaser + deep-link that opens the same anchor for the
                recipient, so we don't lose the reading thread. */}
            <a
              href={buildWhatsAppShareUrl(
                `${s.waTeaser}\n\nRead the chapter → ${
                  typeof window !== "undefined" ? window.location.origin : "https://indianfamilytree.com"
                }/indian-family-tree/story#${s.id}`
              )}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`ift-story-share-wa-${s.id}`}
              className="mt-6 group inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold transition-transform hover:-translate-y-0.5"
              style={{ background: WA_GREEN, color: "#FFFFFF" }}
            >
              <WhatsAppIcon className="w-4 h-4" />
              <span>Send this chapter to family on WhatsApp</span>
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" aria-hidden />
            </a>

            <hr
              className="mt-12 border-0 h-px w-24 mx-auto"
              style={{ background: P.divider, opacity: 0.4 }}
              aria-hidden
            />
          </article>
        ))}
        </div>
      </div>

      {/* ── Closing CTAs ─────────────────────────────────────────── */}
      <section
        style={{ background: P.bgDeep }}
        className="border-t"
        data-testid="ift-story-ctas"
      >
        <div className="max-w-3xl mx-auto px-4 lg:px-6 py-16 text-center">
          <h3
            className="font-display text-3xl sm:text-4xl"
            style={{ color: P.ink, fontFamily: "'Playfair Display', Georgia, serif" }}
          >
            Start your family's chapter today.
          </h3>
          <p className="mt-3 max-w-xl mx-auto text-base" style={{ color: "#4A2A0F" }}>
            Two simple ways to begin — build a living tree for the family
            you love, or create a memorial that honours a life already lived.
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <Link
              to="/family-trees/new"
              data-testid="ift-story-cta-family-tree"
              className="group inline-flex items-center gap-2 rounded-full px-5 py-3 text-sm font-semibold transition-colors"
              style={{ background: P.saffron, color: P.ink }}
            >
              <TreePine className="w-4 h-4" aria-hidden />
              <span>Build Your Family Tree</span>
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" aria-hidden />
            </Link>
            <Link
              to="/obituaries/submit"
              data-testid="ift-story-cta-memorial"
              className="group inline-flex items-center gap-2 rounded-full px-5 py-3 text-sm font-semibold transition-colors border"
              style={{ background: P.card, color: P.ink, borderColor: P.divider }}
            >
              <Flower2 className="w-4 h-4" aria-hidden />
              <span>Celebrate a Life — Memorial</span>
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" aria-hidden />
            </Link>
          </div>

          {/* Group-together CTA — the whole point of IFT is families
              collaborating, so surface a "rally the WhatsApp group" share
              at the bottom too. Separate from per-chapter share because
              the intent differs: this one asks family to co-build; the
              chapter ones simply forward inspiration. */}
          <div
            className="mt-10 mx-auto max-w-xl rounded-2xl px-5 py-6 text-center"
            style={{ background: P.card, border: `1px solid ${P.divider}22` }}
            data-testid="ift-story-group-cta"
          >
            <p className="text-sm sm:text-base" style={{ color: P.ink }}>
              Family trees are a team effort. Invite your parents, siblings,
              cousins and elders to help fill in the missing branches.
            </p>
            <a
              href={buildWhatsAppShareUrl(
                `🪷 I'm building our family tree on IndianFamilyTree.com — will you help?\n\nEvery cousin, aunty and uncle adds a piece of the story. Read why this matters, then let's build it together:\n\n${
                  typeof window !== "undefined" ? window.location.origin : "https://indianfamilytree.com"
                }/indian-family-tree/story`
              )}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="ift-story-share-group"
              className="mt-4 group inline-flex items-center gap-2 rounded-full px-5 py-3 text-sm font-semibold transition-transform hover:-translate-y-0.5"
              style={{ background: WA_GREEN, color: "#FFFFFF" }}
            >
              <WhatsAppIcon className="w-4 h-4" />
              <span>Rally the family on WhatsApp</span>
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" aria-hidden />
            </a>
            <p
              className="mt-3 text-xs"
              style={{ color: WA_GREEN_DEEP }}
            >
              Opens WhatsApp with a ready-to-send invite. You pick the group.
            </p>
          </div>

          {/* Print-friendly QR + short URL card — for community centres,
              church noticeboards, WhatsApp group headers etc. Compact
              enough to sit under the group CTA without competing. */}
          <div className="mt-8 mx-auto max-w-xl" data-testid="ift-story-splink-slot">
            <SplinkCard
              label="IndianFamilyTree · Our Story"
              headline="Print, pin or share as QR"
              testId="ift-story-splink-card"
            />
          </div>
        </div>
      </section>
    </div>
  );
}
